import React, { useMemo, memo } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import Loading from "common/Loading";
import { parseShareInSpeed } from "views/Statistics/DPI/api";
import { getFieldsLanes, getFieldsOf } from "views/Statistics/Report/lanes";
import { getLicenseLimit } from "views/Statistics/Throughput/Protocols/api";
import { NVStackedAreaChartDownsampled } from "common/graphs/DownSampling";
import { tooltipContentGeneratorForUnits } from "common/graphs/NVStackedAreaChart";
import useDashboardData from "./useDashboardData";
import {
  DPI_STATS_DASHBOARD_TMOUT_MS,
  PERIOD_DASHBOARD_TO_HOURS,
  groupIntoRest,
  licenseThresholds,
  retrieveBatch,
} from "./utils";

const Wrapper = styled.div`
  width: 100%;
`;

const WrapperChart = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Subtitle = styled.div`
  font-size: 12px !important;
  color: var(--text-primary);
  margin-top: 10px;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 180px;
  color: var(--text-primary);
`;

// The group of the samples the BQN reports a speed for, and the reading of
// each of them that the chart draws: the ones the DPI hourly statistics use.
const SERIES_GROUP = "services";
const SERIES_VALUE = "speed";

const YAXIS_UNITS = "Mbps";

// The categories drawn, the one of the rest of the traffic included: the BQN
// is asked for one less, as in the DPI hourly statistics, and reports that
// one of its own to complete them.
const CATEGORIES = 10;

// The speed of each category and the limit of the license, which the chart
// draws a line at. Both are asked for in one batch and not in two requests of
// their own: the CLI runs the commands of the GUI one after the other, so two
// of them would take two turns of its queue and no less time than the batch,
// which takes one.
const analyticsRetrievals = ({ hours, mode, bqnId }) => [
  {
    name: "speeds",
    command: `show statistics dpi speed all hours ${hours} interval 60 categories ${
      CATEGORIES - 1
    } direction all interval-speed 5`,
    parse: parseShareInSpeed,
  },
  // A BQN with no license answers no feature, and the chart draws no limit
  // then, as it does when the BQN selected is the whole cluster: the limit is
  // the one of a BQN, and the command reports none for all of them.
  {
    name: "limit",
    command: "show license",
    parse: (response) => getLicenseLimit(response, mode, bqnId),
    mayFail: true,
  },
];

const getDPIAnalytics = ({ period, mode, bqnId }) =>
  retrieveBatch(
    analyticsRetrievals({ hours: PERIOD_DASHBOARD_TO_HOURS[period], mode, bqnId }),
    mode,
    bqnId,
    { timeout: DPI_STATS_DASHBOARD_TMOUT_MS }
  );

function DPIAnalytics({ period, range }) {
  // The range of the dashboard is the clock of the chart, which is asked for
  // again with each new one of them: a refresh of the page loads the range,
  // so the chart takes no turn of the CLI of its own for it.
  const { data: dataRange, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId, range }) =>
      getDPIAnalytics({ period, mode, bqnId }).then((data) => ({
        data,
        range,
      })),
    onError: "Error fetching the DPI analytics data:",
    deps: [period],
    range,
  });

  const { data, range: rangeDrawn } = dataRange || {};
  const { speeds, limit } = data || {};

  // The categories the BQN reports become the fields of the chart, and each
  // sample of them a value per field, the way the DPI hourly statistics draw
  // them. They are what the chart compares to decide whether it has to be
  // drawn again, as the thresholds are, so all of them keep their identity
  // while the samples are the same.
  const categories = useMemo(
    () => getFieldsOf(SERIES_GROUP)(speeds || []).map(({ name }) => name),
    [speeds]
  );
  const lanes = useMemo(
    () => getFieldsLanes(SERIES_GROUP, SERIES_VALUE)(speeds || []),
    [speeds]
  );

  // The command asks the BQN for the categories the legend of the card holds
  // and leaves the rest of the traffic in one of its own, so it reports no
  // more than those. A BQN that reported more would fill the legend and the
  // tooltip of the chart, which name the areas it is given, so what is over
  // them is drawn as one area that adds up the speed of all of it.
  const { participants: categoriesShown, items } = useMemo(
    () => groupIntoRest(categories, lanes),
    [categories, lanes]
  );
  const fields = useMemo(
    () => categoriesShown.map((name) => ({ name, label: name })),
    [categoriesShown]
  );
  const thresholds = useMemo(
    () => licenseThresholds(limit, items),
    [limit, items]
  );

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (items.length === 0 || fields.length === 0) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  return (
    <Wrapper>
      <Subtitle>Downlink + Uplink.</Subtitle>
      <WrapperChart>
        <NVStackedAreaChartDownsampled
          copyEndpoints={true}
          xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
          xField="time"
          fields={fields}
          items={items}
          range={rangeDrawn}
          yAxisUnits={YAXIS_UNITS}
          tooltipContentGenerator={tooltipContentGeneratorForUnits(
            YAXIS_UNITS,
            undefined
          )}
          yAxisTopGap={0.05}
          margin={{ top: 5, right: 10, bottom: 20, left: 60 }}
          xAxisTicksNumber={6}
          thresholds={thresholds}
        />
      </WrapperChart>
    </Wrapper>
  );
}

export default memo(DPIAnalytics);
